import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import kpiCategoryService, { KPICategory, ImportResult } from '../services/kpiCategoryService';
import categoryKPIService, { CategoryKPI, CreateCategoryKPIRequest } from '../services/categoryKPIService';
import KPIManagementModal from '../components/kpi/KPIManagementModal';
import KPIImportModal from '../components/kpi/KPIImportModal';
import { downloadKPIImportTemplate } from '../utils/csvTemplateGenerator';
import './OrganisationKPIsPage.css';

const OrganisationKPIsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [categories, setCategories] = useState<KPICategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // KPI Management Modal State
  const [selectedCategory, setSelectedCategory] = useState<KPICategory | null>(null);
  const [categoryKPIs, setCategoryKPIs] = useState<CategoryKPI[]>([]);
  const [measurementTypes, setMeasurementTypes] = useState<string[]>([]);
  const [kpiModalOpen, setKpiModalOpen] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);
  
  // Main page KPI display state
  const [allCategoryKPIs, setAllCategoryKPIs] = useState<{[categoryId: number]: CategoryKPI[]}>({});
  
  // Drag and drop state
  const [draggedCategory, setDraggedCategory] = useState<KPICategory | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Expanded card state
  const [expandedCardId, setExpandedCardId] = useState<number | null>(null);
  
  // Import functionality state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult | null>(null);

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await kpiCategoryService.getKPICategories();
      setCategories(data);
      
      // Fetch KPIs for each category
      await fetchAllKPIs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load KPI categories');
      console.error('Error fetching KPI categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllKPIs = async (categories: KPICategory[]) => {
    try {
      const kpiPromises = categories.map(async (category) => {
        const kpis = await categoryKPIService.getCategoryKPIs(category.id!);
        return { categoryId: category.id!, kpis };
      });
      
      const results = await Promise.all(kpiPromises);
      
      const kpiMap: {[categoryId: number]: CategoryKPI[]} = {};
      results.forEach(({ categoryId, kpis }) => {
        kpiMap[categoryId] = kpis;
      });
      
      setAllCategoryKPIs(kpiMap);
    } catch (err: any) {
      console.error('Error fetching KPIs:', err);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    const tempCategory: KPICategory = {
      id: Date.now(), // Temporary ID
      organization_id: user?.organization_id || 0,
      category_name: newCategoryName.trim(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      setCreating(true);
      
      // Optimistic update - add to UI immediately
      setCategories(prev => [...prev, tempCategory]);
      setNewCategoryName('');
      setIsCreatingCategory(false);
      
      // Create in backend
      const createdCategory = await kpiCategoryService.createKPICategory({
        category_name: tempCategory.category_name
      });
      
      // Replace temp category with real one
      setCategories(prev => 
        prev.map(cat => cat.id === tempCategory.id ? createdCategory : cat)
      );
    } catch (err: any) {
      // Rollback on error
      setCategories(prev => prev.filter(cat => cat.id !== tempCategory.id));
      setError(err.message || 'Failed to create KPI category');
      setIsCreatingCategory(true); // Re-open form
      setNewCategoryName(tempCategory.category_name); // Restore text
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setNewCategoryName('');
    setIsCreatingCategory(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCreateCategory();
    } else if (e.key === 'Escape') {
      handleCancelCreate();
    }
  };

  const handleEditCategory = (category: KPICategory) => {
    setEditingCategoryId(category.id!);
    setEditingCategoryName(category.category_name);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategoryName.trim() || !editingCategoryId) return;

    const oldCategory = categories.find(cat => cat.id === editingCategoryId);
    if (!oldCategory) return;

    try {
      setUpdating(true);
      
      // Optimistic update - update UI immediately
      setCategories(prev => 
        prev.map(cat => 
          cat.id === editingCategoryId 
            ? { ...cat, category_name: editingCategoryName.trim(), updated_at: new Date().toISOString() }
            : cat
        )
      );
      setEditingCategoryId(null);
      setEditingCategoryName('');
      
      // Update in backend
      await kpiCategoryService.updateKPICategory(editingCategoryId, {
        category_name: editingCategoryName.trim()
      });
      
    } catch (err: any) {
      // Rollback on error
      setCategories(prev => 
        prev.map(cat => 
          cat.id === editingCategoryId ? oldCategory : cat
        )
      );
      setError(err.message || 'Failed to update KPI category');
      setEditingCategoryId(editingCategoryId); // Re-open edit
      setEditingCategoryName(editingCategoryName); // Restore text
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleUpdateCategory();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete the category "${name}"?`)) {
      return;
    }

    const categoryToDelete = categories.find(cat => cat.id === id);
    if (!categoryToDelete) return;

    try {
      // Optimistic update - remove from UI immediately
      setCategories(prev => prev.filter(cat => cat.id !== id));
      
      // Delete from backend
      await kpiCategoryService.deleteKPICategory(id);
      
    } catch (err: any) {
      // Rollback on error - restore the deleted category
      setCategories(prev => [...prev, categoryToDelete].sort((a, b) => a.id! - b.id!));
      setError(err.message || 'Failed to delete KPI category');
    }
  };

  useEffect(() => {
    if (user?.organization_id || user?.is_super_admin) {
      fetchCategories();
      fetchMeasurementTypes();
    } else {
      setError('User does not belong to an organization');
      setLoading(false);
    }
  }, [user]);

  const fetchMeasurementTypes = async () => {
    try {
      const types = await categoryKPIService.getMeasurementTypes();
      setMeasurementTypes(types);
    } catch (err) {
      console.error('Error fetching measurement types:', err);
    }
  };

  const handleOpenKPIModal = async (category: KPICategory) => {
    try {
      setSelectedCategory(category);
      setCategoryKPIs([]); // Clear previous KPIs
      setKpiLoading(true);
      
      // Fetch KPIs for this category BEFORE opening modal
      console.log('Fetching KPIs for category ID:', category.id);
      const kpis = await categoryKPIService.getCategoryKPIs(category.id!);
      console.log('Fetched KPIs:', kpis);
      console.log('KPIs array length:', kpis.length);
      setCategoryKPIs(kpis);
      
      // Open modal after KPIs are loaded
      setKpiModalOpen(true);
    } catch (err: any) {
      console.error('Error fetching KPIs:', err);
      setError(err.message || 'Failed to load KPIs');
      setCategoryKPIs([]);
      // Still open modal even if there's an error, so user can see the error state
      setKpiModalOpen(true);
    } finally {
      setKpiLoading(false);
    }
  };

  const handleCloseKPIModal = () => {
    setKpiModalOpen(false);
    setSelectedCategory(null);
    setCategoryKPIs([]);
  };

  const handleCreateKPI = async (kpiData: CreateCategoryKPIRequest) => {
    try {
      const newKPI = await categoryKPIService.createCategoryKPI(kpiData);
      
      // Update both modal state and main page state
      setCategoryKPIs(prev => [...prev, newKPI]);
      
      // Update the main page display state
      setAllCategoryKPIs(prev => ({
        ...prev,
        [kpiData.categoryId]: [...(prev[kpiData.categoryId] || []), newKPI]
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to create KPI');
      throw err; // Re-throw to let modal handle the error state
    }
  };

  const handleUpdateKPI = async (id: number, kpiData: Partial<CategoryKPI>) => {
    const oldKPI = categoryKPIs.find(kpi => kpi.id === id);
    if (!oldKPI) return;

    const updatedKPI = { ...oldKPI, ...kpiData, updatedAt: new Date().toISOString() };

    try {
      // Optimistic update for both states
      setCategoryKPIs(prev => 
        prev.map(kpi => kpi.id === id ? updatedKPI : kpi)
      );

      // Update main page state
      setAllCategoryKPIs(prev => ({
        ...prev,
        [oldKPI.categoryId]: prev[oldKPI.categoryId]?.map(kpi => 
          kpi.id === id ? updatedKPI : kpi
        ) || []
      }));

      await categoryKPIService.updateCategoryKPI(id, kpiData);
    } catch (err: any) {
      // Rollback on error for both states
      setCategoryKPIs(prev => 
        prev.map(kpi => kpi.id === id ? oldKPI : kpi)
      );
      setAllCategoryKPIs(prev => ({
        ...prev,
        [oldKPI.categoryId]: prev[oldKPI.categoryId]?.map(kpi => 
          kpi.id === id ? oldKPI : kpi
        ) || []
      }));
      setError(err.message || 'Failed to update KPI');
      throw err;
    }
  };

  const handleDeleteKPI = async (id: number, name: string) => {
    const kpiToDelete = categoryKPIs.find(kpi => kpi.id === id);
    if (!kpiToDelete) return;

    try {
      // Optimistic update for both states
      setCategoryKPIs(prev => prev.filter(kpi => kpi.id !== id));
      
      // Update main page state
      setAllCategoryKPIs(prev => ({
        ...prev,
        [kpiToDelete.categoryId]: prev[kpiToDelete.categoryId]?.filter(kpi => kpi.id !== id) || []
      }));

      await categoryKPIService.deleteCategoryKPI(id);
    } catch (err: any) {
      // Rollback on error for both states
      setCategoryKPIs(prev => [...prev, kpiToDelete].sort((a, b) => a.displayOrder - b.displayOrder));
      setAllCategoryKPIs(prev => ({
        ...prev,
        [kpiToDelete.categoryId]: [...(prev[kpiToDelete.categoryId] || []), kpiToDelete].sort((a, b) => a.displayOrder - b.displayOrder)
      }));
      setError(err.message || 'Failed to delete KPI');
      throw err;
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, category: KPICategory) => {
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    
    // Add some visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedCategory(null);
    setDragOverIndex(null);
    
    // Reset visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear drag over if we're leaving the card entirely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    if (!draggedCategory) return;

    const draggedIndex = categories.findIndex(cat => cat.id === draggedCategory.id);
    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDragOverIndex(null);
      return;
    }

    // Reorder categories array
    const newCategories = [...categories];
    const [removed] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(targetIndex, 0, removed);

    setCategories(newCategories);
    setDragOverIndex(null);
    
    // TODO: If you want to persist the order, you could call an API here
    // await kpiCategoryService.updateCategoryOrder(newCategories.map(cat => cat.id));
  };

  // Double-click handler for card expansion
  const handleCardDoubleClick = (categoryId: number) => {
    // Don't expand if card is being edited
    if (editingCategoryId === categoryId) return;
    
    // Toggle expansion - if already expanded, collapse it, otherwise expand it
    setExpandedCardId(expandedCardId === categoryId ? null : categoryId);
  };

  // Click outside handler to collapse expanded card
  const handleClickOutside = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.category-card') && expandedCardId !== null) {
      setExpandedCardId(null);
    }
  };

  // Import functionality handlers
  const handleDownloadTemplate = () => {
    try {
      downloadKPIImportTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template');
    }
  };

  const handleImportComplete = async (results: ImportResult) => {
    setImportResults(results);
    setImportModalOpen(false);
    
    // Refresh categories list
    await fetchCategories();
    
    // Show success message
    if (results.success) {
      console.log(`Import successful: ${results.categoriesCreated} categories, ${results.kpisCreated} KPIs created`);
    } else {
      console.log('Import completed with errors:', results.errors);
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content" onClick={handleClickOutside}>
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Organisation KPIs</span>
          </div>
          
          <div className="content-container">
            <div className="page-header">
              <h1>Organisation KPIs</h1>
            </div>

            {/* Header Actions Section */}
            <div className="page-header-actions">
              <div className="header-actions-left">
                <h2>KPI Categories & Management</h2>
                <p className="page-subtitle">Manage your organization's KPI categories and indicators</p>
              </div>
              
              <div className="header-actions-right">
                <button 
                  className="template-btn secondary-btn"
                  onClick={handleDownloadTemplate}
                  disabled={loading}
                >
                  📄 Download Template
                </button>
                
                <button 
                  className="import-btn primary-btn"
                  onClick={() => setImportModalOpen(true)}
                  disabled={loading}
                >
                  📤 Import from CSV
                </button>
              </div>
            </div>

            {error && (
              <div className="error-alert">
                <p>{error}</p>
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}

            <div className="content-section">
              <div className="section-header">
                <h2>KPI Categories</h2>
              </div>



              {loading ? (
                <div className="loading-message">Loading KPI categories...</div>
              ) : (
                <div className="categories-grid">
                  {categories.map((category, index) => (
                    <div 
                      key={category.id} 
                      className={`category-card ${editingCategoryId === category.id ? 'editing' : ''} ${dragOverIndex === index ? 'drag-over' : ''} ${draggedCategory?.id === category.id ? 'dragging' : ''} ${expandedCardId === category.id ? 'expanded' : ''}`}
                      draggable={editingCategoryId !== category.id}
                      onDragStart={(e) => handleDragStart(e, category)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDoubleClick={() => handleCardDoubleClick(category.id!)}
                    >
                      <div className="category-header">
                        <div className="category-title">
                          {editingCategoryId === category.id ? (
                            <input
                              type="text"
                              value={editingCategoryName}
                              onChange={(e) => setEditingCategoryName(e.target.value)}
                              onKeyDown={handleEditKeyPress}
                              placeholder="Enter category name..."
                              className="inline-category-input"
                              autoFocus
                              disabled={updating}
                            />
                          ) : (
                            <h3>{category.category_name}</h3>
                          )}
                        </div>
                        <div className="category-actions">
                          {editingCategoryId === category.id ? (
                            <div className="inline-actions">
                              <button 
                                className="action-button save-button"
                                onClick={handleUpdateCategory}
                                disabled={updating || !editingCategoryName.trim()}
                                title="Save changes (Enter)"
                              >
                                {updating ? '⏳' : '✓'}
                              </button>
                              <button 
                                className="action-button cancel-button"
                                onClick={handleCancelEdit}
                                disabled={updating}
                                title="Cancel (Escape)"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <>
                              <button 
                                className="action-button edit-button"
                                onClick={() => handleEditCategory(category)}
                                title="Edit category"
                              >
                                ✏️
                              </button>
                              <button 
                                className="action-button delete-button"
                                onClick={() => handleDeleteCategory(category.id!, category.category_name)}
                                title="Delete category"
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="category-content">
                        {allCategoryKPIs[category.id!] && allCategoryKPIs[category.id!].length > 0 ? (
                          <div className={`kpi-list-display ${expandedCardId === category.id ? 'expanded' : ''}`}>
                            {allCategoryKPIs[category.id!].map((kpi) => (
                              <div key={kpi.id} className="kpi-item-display">
                                <span className="kpi-name-display">{kpi.kpiName}</span>
                                <span className="kpi-type-display">{kpi.measurementType}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="kpi-placeholder">
                            <p className="placeholder-text">No KPIs defined</p>
                            {expandedCardId === category.id && (
                              <p className="placeholder-expanded-text">Double-click to collapse this view, or click "Add/Edit KPIs" to get started with your first KPI.</p>
                            )}
                          </div>
                        )}
                        <button 
                          className="add-kpi-button"
                          onClick={() => handleOpenKPIModal(category)}
                          disabled={editingCategoryId === category.id}
                        >
                          + Add/Edit KPIs
                        </button>
                        {expandedCardId === category.id ? (
                          <div className="expanded-actions">
                            <p className="expand-hint">Double-click anywhere on the card to collapse</p>
                          </div>
                        ) : (
                          allCategoryKPIs[category.id!] && allCategoryKPIs[category.id!].length > 3 && (
                            <div className="more-kpis-hint">
                              <p className="more-kpis-text">Double-click to view all {allCategoryKPIs[category.id!].length} KPIs</p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Category Card */}
                  <div className={`category-card add-category-card ${isCreatingCategory ? 'editing' : ''}`}>
                    <div className="category-header">
                      <div className="category-title">
                        {isCreatingCategory ? (
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Enter category name..."
                            className="inline-category-input"
                            autoFocus
                            disabled={creating}
                          />
                        ) : (
                          <h3 onClick={() => setIsCreatingCategory(true)}>+ Add New Category</h3>
                        )}
                      </div>
                      {isCreatingCategory && (
                        <div className="inline-actions">
                          <button 
                            className="action-button save-button"
                            onClick={handleCreateCategory}
                            disabled={creating || !newCategoryName.trim()}
                            title="Save category (Enter)"
                          >
                            {creating ? '⏳' : '✓'}
                          </button>
                          <button 
                            className="action-button cancel-button"
                            onClick={handleCancelCreate}
                            disabled={creating}
                            title="Cancel (Escape)"
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="category-content">
                      <div className="add-category-content">
                        {!isCreatingCategory && (
                          <>
                            <div className="add-category-icon">📊</div>
                            <p className="add-category-text">Click to create a new KPI category</p>
                          </>
                        )}
                        {isCreatingCategory && (
                          <div className="create-instructions">
                            <p className="instruction-text">Type category name and press Enter to save</p>
                            <p className="instruction-text">Press Escape to cancel</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}


            </div>
          </div>
        </div>
      </div>

      {/* KPI Management Modal */}
      {selectedCategory && (
        <KPIManagementModal
          isOpen={kpiModalOpen}
          onClose={handleCloseKPIModal}
          categoryId={selectedCategory.id!}
          categoryName={selectedCategory.category_name}
          kpis={categoryKPIs}
          measurementTypes={measurementTypes}
          onCreateKPI={handleCreateKPI}
          onUpdateKPI={handleUpdateKPI}
          onDeleteKPI={handleDeleteKPI}
          loading={kpiLoading}
        />
      )}

      {/* KPI Import Modal */}
      <KPIImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default OrganisationKPIsPage;